import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AndOfErrors } from 'v2/core/errors/errors';
import useGetIntegrationEditFormData from 'v2/core/repository/edge-server/integrations/editIntegrations/useGetIntegrationEditFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

interface TypeformFormData {
  id: string;
  title: string;
}

export const EditTypeformIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
}: EditIntegrationDrawerContentProps) => {
  const [updateIntegration, { status: uStatus, error: uError }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Typeform,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Typeform,
          integrationID,
        });
      },
    });

  const {
    data: fData,
    status: fStatus,
    error: fError,
  } = useGetIntegrationEditFormData({ integrationID });

  const allForms = useMemo(
    () => fData?.typeform?.forms ?? [],
    [fData?.typeform?.forms],
  );

  const savedForms = useMemo(
    () => integrationData.typeform?.includedForms ?? [],
    [integrationData.typeform?.includedForms],
  );

  const [selectedForms, setSelectedForms] =
    useState<TypeformFormData[]>(savedForms);

  useEffect(() => {
    if (savedForms.length > 0) {
      setSelectedForms(savedForms);
    }
  }, [savedForms]);

  const handleSubmit = () => {
    updateIntegration({
      integrationID,
      isActive: true,
      updateData: {
        typeform: {
          includedForms: selectedForms,
        },
      },
    });
  };

  const isIntegrationDataLoading = IsLoading(fStatus);

  const error = AndOfErrors([fError, uError]);
  useErrorHandler(error);

  return (
    <>
      <AddOrEditIntegrationDrawerHeader title="Edit Typeform Integration" />
      <Stack p={3} spacing={3}>
        <Autocomplete
          fullWidth
          multiple
          limitTags={10}
          disableCloseOnSelect
          options={allForms}
          value={selectedForms}
          getOptionLabel={(option) => option.title}
          onChange={(_, data) => setSelectedForms(data)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select forms" />
          )}
          renderOption={(props, option, { selected }) => {
            return (
              <Box {...props} component="li" style={{ padding: '2px' }}>
                <Stack alignItems="center" direction="row">
                  <Checkbox size="small" checked={selected} />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body1">{option.title}</Typography>
                  </Stack>
                </Stack>
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          componentsProps={{ paper: { elevation: 8 } }}
          loading={isIntegrationDataLoading}
        />
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          loading={IsLoading(uStatus)}
        >
          Save Changes
        </LoadingButton>
      </Stack>
    </>
  );
};
